<template>
  <q-card
    class="med-dialog-card"
    @keydown="windowKeyDown"
    :style="cardSizeStyle"
  >
    <q-card-section
      class="med-header row"
      :class="{ 'med-header--prompt': isPromtDialog }"
    >
      <img
        v-if="isPromtDialog && isDangerAction"
        :src="warningIcon"
        class="med-header__icon"
        alt=""
      />
      <div
        :class="[
          '',
          { 'med-header__title': isPromtDialog },
          { 'text-subtitle2': !isPromtDialog },
        ]"
        :style="isPromtDialog ? '' : 'max-width: calc(100% - 30px)'"
      >
        {{ isPromtDialog ? promptTitle : windowTitle }}
      </div>
      <template v-if="!isPromtDialog">
        <q-space />
        <q-btn
          size="sm"
          icon="close"
          class="q-my-none unset-items-center"
          flat
          round
          dense
          @click="closeItemMenu"
        />
      </template>
    </q-card-section>

    <q-card-section v-if="isPromtDialog" class="med-prompt-text">
      <template v-if="checkingRights">{{ $t("checking_rights") }}</template>
      <span v-else v-html="promptTextHtml"></span>
    </q-card-section>

    <q-separator v-if="!isPromtDialog" class="med-separator" />

    <q-card-section v-if="!isPromtDialog" class="scroll med-body">
      <!-- Analytic Lists -->
      <div
        v-if="modelType === 5 && parameterMode === 1"
        class="field-group med-comment"
      >
        <mosta-tree-select
          ref="list_select"
          :display-text="listDisplayText"
          :placeholder="$t('not_selected_label')"
          :show-free-input="true"
          :free-input-tooltip="$t('create_list')"
          :content-max-height="360"
          :dropdown-height="320"
          @update:search="(val) => { aux_filter_string = val; }"
          @keyDown="$refs.aux_selector && $refs.aux_selector.trackArrowDown(1)"
          @keyUp="$refs.aux_selector && $refs.aux_selector.trackArrowUp(1)"
          @keyEnter="onListSelectEnter"
          @freeInputPressed="onListFreeInput"
        >
          <aux-selector
            ref="aux_selector"
            :filter-model-value="aux_filter_string"
            @update:filter-model-value="(arg) => { aux_filter_string = arg; }"
            :model-value="[]"
            :show-search-header="false"
            @update:model-value="(arg) => { onUpdateListModel(arg); }"
            @input-value="(val) => { onUpdateListModelNew(val); }"
            :allow-free-input="true"
            :allow-multiple="true"
            :model-type="17"
            :limit-height-to-container="true"
            :container-height-px="240"
            :exclude-id="srcElement.elType == 6 ? srcElement.Id : 0"
            :preferred-segment="srcElement.DestType"
            @selectorClosed="hideListMenu"
          />
        </mosta-tree-select>
      </div>

      <!-- Объединённое окно: цель правки (инфо, комментарий или параметр), режим и значение -->
      <div v-if="isUnifiedEditWindow" class="field-group med-comment">
        <div class="med-unified__row">
          <mosta-tree-select
            ref="target_select"
            class="med-unified__target"
            :display-text="targetDisplayText"
            :placeholder="$t('select_parameter')"
            @update:search="(val) => { filter_string = val; }"
            @keyDown="$refs.rec_selector && $refs.rec_selector.trackArrowDown(1)"
            @keyUp="$refs.rec_selector && $refs.rec_selector.trackArrowUp(1)"
            @keyEnter="$refs.rec_selector && $refs.rec_selector.selectCurrent()"
          >
            <div
              v-if="canTargetDetails"
              class="med-unified__pinned"
              :class="{ 'med-unified__pinned--active': editTarget.kind === 'details' }"
              @click="pickNotesTarget('details')"
            >
              {{ $t("details_type") }}
            </div>
            <div
              v-if="canTargetComment"
              class="med-unified__pinned"
              :class="{ 'med-unified__pinned--active': editTarget.kind === 'comment' }"
              @click="pickNotesTarget('comment')"
            >
              {{ $t("notes_type") }}
            </div>
            <div
              v-if="canTargetParameters && (canTargetDetails || canTargetComment)"
              class="med-unified__sep"
            ></div>
            <loading-skeleton v-if="canTargetParameters && parametersLoading" />
            <recursive-selector
              v-else-if="canTargetParameters"
              ref="rec_selector"
              :elements="parametersList"
              :take-ids-only="true"
              :show-records="true"
              :allow-multiple="false"
              :element-type="9"
              v-model="parametersModel"
              :hide-checkboxes="true"
              return-type="chain"
              nested-key-name="RecordFields"
              entity-inside-name="Template"
              :filter-out-list="[7]"
              :filter-string="filter_string"
            />
          </mosta-tree-select>

          <mosta-select
            class="med-unified__mode"
            v-model="commentChangeType"
            option-label="Name"
            :options="changeCommentOptions"
            :disable="!isNotesTarget"
          />
        </div>

        <!-- Значение параметра. Список параметра идёт строками: плюс у последней, крестик у остальных -->
        <template v-if="editTarget.kind === 'parameter'">
          <div
            v-for="(param_row, param_index) in parameterContainer ? paramRows : []"
            :key="'pr_' + param_index"
            class="med-comment__row"
          >
            <multi-edit-value-row
              v-model="paramRows[param_index]"
              :parameter-value-type="parameterContainer.ValueType"
              :parameter-set-value-type="
                parameterContainer.ValueSet
                  ? parameterContainer.ValueSet.ValueType
                  : 0
              "
              :vset-type="vsetType"
              :vset-title="vsetTitle"
              :vsetOptions="vsetOptions"
              :vset-loading="vsetLoading"
            />
            <button
              v-if="isListParameter && param_index === paramRows.length - 1"
              type="button"
              class="med-unified__row-btn"
              :aria-label="$t('add_list_param_value')"
              @click.stop="addParamRow"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3.33333V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                <path d="M3.33333 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              <q-tooltip
                :delay="300"
                anchor="top middle"
                self="bottom middle"
                :offset="[0, 4]"
                class="med-tooltip"
              >
                {{ $t('add_list_param_value') }}
              </q-tooltip>
            </button>
            <button
              v-else-if="isListParameter"
              type="button"
              class="med-unified__row-btn med-unified__row-btn--del"
              :aria-label="$t('delete_list_param_value')"
              tabindex="-1"
              @click.stop="deleteParamRow(param_index)"
            >
              <span class="med-unified__del-ico"></span>
              <q-tooltip
                :delay="300"
                anchor="top middle"
                self="bottom middle"
                :offset="[0, 4]"
                class="med-tooltip"
              >
                {{ $t('delete_list_param_value') }}
              </q-tooltip>
            </button>
            <button
              v-else
              type="button"
              class="med-comment__tmpl-btn"
              disabled
              :aria-label="$t('templates')"
              tabindex="-1"
            >
              <span class="med-comment__tmpl-ico"></span>
            </button>
          </div>
          <div v-if="!parameterContainer" class="med-comment__row">
            <native-input
              class="med-comment__input"
              style="width: -moz-available; width: -webkit-fill-available"
              :show-border="true"
              :disabled-field="true"
              :model-value="''"
            />
            <button
              type="button"
              class="med-comment__tmpl-btn"
              disabled
              :aria-label="$t('templates')"
              tabindex="-1"
            >
              <span class="med-comment__tmpl-ico"></span>
            </button>
          </div>
        </template>

        <div v-else class="med-comment__row">
          <native-input
            ref="comment_field"
            class="med-comment__input"
            style="width: -moz-available; width: -webkit-fill-available"
            :show-border="true"
            :model-value="innerNote"
            @update:caretPosition="onCaretPositionChange"
            @click="onNativeInputClick"
            @blur="onCommentBlur"
            @update:modelValue="
              (newname) => {
                innerNote = newname;
              }
            "
            :edit-on-click="true"
            :clear-hotkey="true"
          />
          <button
            ref="commentTemplateBtn"
            type="button"
            class="med-comment__tmpl-btn"
            :class="{ 'med-comment__tmpl-btn--active': commentTemplateMenuOpen }"
            :aria-label="$t(templatesTooltipKey)"
            @mousedown.prevent
            @click.stop="openCommentTemplateMenu"
          >
            <span class="med-comment__tmpl-ico"></span>
            <q-tooltip
              :delay="300"
              anchor="top middle"
              self="center middle"
              class="med-tooltip"
            >
              {{ $t(templatesTooltipKey) }}
            </q-tooltip>
          </button>
        </div>
        <mosta-context-menu
          :visible="commentTemplateMenuOpen"
          :position="commentTemplateMenuPos"
          :close-on-esc="true"
          @close="closeCommentTemplateMenu"
        >
          <!-- focusin.stop: q-dialog ловит focusin на body и возвращает фокус себе,
               иначе курсор не удержится в поле поиска телепортированного меню. -->
          <div class="med-comment__tmpl-menu" @focusin.stop>
            <aux-selector
              :model-type="commentTemplateModelType"
              :preferred-segment="srcElement?.DestType"
              :input-index="0"
              :allow-multiple="false"
              :allow-free-input="true"
              @deleteItem="deleteCommentTemplate"
              @update:caretPosition="onCaretPositionChange"
              v-model="comment_templates"
              @input-value="
                (val) => {
                  addCommentTemplate(val);
                }
              "
              @selectorClosed="closeCommentTemplateMenu"
            />
          </div>
        </mosta-context-menu>
      </div>

      <div
        v-if="modelType === 1 && [1, 2].includes(parameterMode)"
        class="field-group med-comment med-multi"
      >
        <mosta-tree-select
          ref="tags_select"
          :display-text="tagsDisplayText"
          :placeholder="$t('tags_upcase')"
          :show-search-header="false"
          :content-max-height="360"
          :dropdown-height="320"
        >
          <aux-selector
            ref="tags_aux_selector"
            v-model="tagsSelectorModel"
            :model-type="1"
            :allow-multiple="true"
            :limit-height-to-container="true"
            :full="false"
            :allow-free-input="true"
            :container-height-px="240"
            @input-value="onAddFreeTag"
            @selectorClosed="closeTagsSelect"
          />
        </mosta-tree-select>
        <div v-if="tags.length" class="med-multi__chips">
          <span
            v-for="(tag, chip_index) in tags"
            :key="'tagchip_' + chip_index"
            class="med-multi__chip"
          >
            {{ tagChipLabel(tag) }}
            <button
              type="button"
              class="med-multi__chip-btn"
              :aria-label="$t('remove')"
              tabindex="-1"
              @click.stop="removeSelectedTag(chip_index)"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                <path d="M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </button>
          </span>
        </div>
      </div>

      <div
        v-else-if="modelType === 1 && parameterMode === 3"
        class="field-group med-comment"
      >
        <mosta-select
          v-model="tagStatusModel"
          :options="tagStatusOptions"
          option-label="label"
          :label="$t('status_label')"
        />
      </div>

      <!-- Добавление и удаление параметров. Раскладка та же, что в окне правки:
           режим и кнопка шаблонов тут не работают, поэтому просто задизейблены -->
      <div
        v-if="modelType === 15 && [1, 3].includes(parameterMode)"
        class="field-group med-comment med-multi"
      >
        <div class="med-unified__row">
          <mosta-tree-select
            ref="multi_target_select"
            class="med-unified__target"
            :display-text="multipleParametersDisplayText"
            :placeholder="$t('select_parameters')"
            @update:search="(val) => { filter_string = val; }"
            @keyDown="$refs.rec_selector && $refs.rec_selector.trackArrowDown(1)"
            @keyUp="$refs.rec_selector && $refs.rec_selector.trackArrowUp(1)"
            @keyEnter="$refs.rec_selector && $refs.rec_selector.selectCurrent()"
          >
            <loading-skeleton v-if="parametersLoading" />
            <recursive-selector
              v-else
              ref="rec_selector"
              :elements="parametersList"
              :take-ids-only="true"
              :show-records="false"
              :allow-multiple="true"
              :element-type="9"
              v-model="multipleParametersModelChains"
              :hide-checkboxes="false"
              return-type="chain"
              nested-key-name="RecordFields"
              entity-inside-name="Template"
              :filter-string="filter_string"
            />
          </mosta-tree-select>

          <mosta-select
            class="med-unified__mode"
            v-model="commentChangeType"
            option-label="Name"
            :options="changeCommentOptions"
            :disable="true"
          />
        </div>

        <div class="med-comment__row">
          <native-input
            class="med-comment__input"
            style="width: -moz-available; width: -webkit-fill-available"
            :show-border="true"
            :disabled-field="true"
            :model-value="''"
          />
          <button
            type="button"
            class="med-comment__tmpl-btn"
            disabled
            :aria-label="$t('templates')"
            tabindex="-1"
          >
            <span class="med-comment__tmpl-ico"></span>
          </button>
        </div>

        <div v-if="multipleParametersModel.length" class="med-multi__chips">
          <span
            v-for="(param, chip_index) in multipleParametersModel"
            :key="'chip_' + param.Id + '_' + chip_index"
            class="med-multi__chip"
          >
            {{ param.Name }}
            <button
              type="button"
              class="med-multi__chip-btn"
              :aria-label="$t('remove')"
              tabindex="-1"
              @click.stop="removeSelectedParameter(chip_index)"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                <path d="M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </button>
          </span>
        </div>
      </div>

      <div
        v-if="msgText"
        class="med-msg"
      >
        {{ msgText }}
      </div>
    </q-card-section>

    <q-separator v-if="!isPromtDialog" class="med-separator" />

    <q-card-actions class="med-actions">
      <button
        type="button"
        class="med-btn med-btn--cancel"
        ref="cancelbtn"
        @click="closeItemMenu"
      >
        {{ operation_passed ? $t("close_label") : $t("cancel_short") }}
      </button>
      <button
        type="button"
        class="med-btn"
        :class="isDangerAction ? 'med-btn--danger' : 'med-btn--confirm'"
        ref="okbtn"
        :disabled="checkingRights || saveDisabled || actionRunning || forbidden"
        @click="performAction"
        v-if="!operation_passed"
      >
        {{ primaryActionLabel }}
      </button>
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from "vue-facing-decorator";
// import TagSelector from "@/components/common/aux-selector/TagSelector.vue";
import { CommonEntity } from "@/models/interfaces/common";
import RecursiveSelector from "@/components/common/basic/recursive-selector/refactored/RecursiveSelector.vue";
import {
  ParameterInstanceCreateContainer,
  ParameterTemplateForMultiEdit,
  ParameterValueSetDefaultHintContainer,
} from "@/models/interfaces/entity-settings/parameters/parameters";
import { multiEditStore } from "@/store/multiedit";
import { reactive } from "vue";
import {
  arrayEquals,
  findRecursive,
} from "@/models/helpers/recursive";
import { auxStore } from "@/store/auxillary";
import { TagContainer } from "@/models/interfaces/tags/tag-models";
import {
  BaseListItem,
  getIdentityKind,
} from "../common/base-list/interfaces/ListTypes";
import {
  PreCheckResult,
  V2ParameterValueSetExtensionType,
  V2ParameterValueSet_ValueType,
  V2ParameterValueType,
} from "@/models/etc/parameter-types";
import { findParametersByChain } from "@/utils/formulas";
import { ParamsRow } from "./MultiEditLine.vue";
import { AnalyticsV2ChartType, AnalyticsV2ElementType, TagType } from "@/models/etc/types";
import { i18n } from "@/i18n";
import { AnalyticsChartIdentity, AnalyticsElementIdentity } from "@/models/interfaces/entity-settings/charts/charts";
import { SelectionEntityWithRolesFilterSend } from "@/models/interfaces/entity-settings/filters/filters";
import NativeInput from "../common/basic/NativeInput.vue";
import { StorageDetailsTemplateDto } from "../../models/interfaces/storage/storage-models";
import { storagePositionsStore } from "../../store/storage/storage-positions";
import { storageMovementsStore } from "../../store/storage/storage-movements";
import { analyticsCommentsStore } from "../../store/analytics-comments";
import { MultiEditCommentsRequest, MultiEditStorageDetailsEditType } from "../../store/interfaces/types.analytics";
import { BaseItemTypes } from "../dnd/ItemTypes";
import MostaSelect from '@/components/common/MostaSelect.vue';
import MostaTreeSelect from '@/components/common/MostaTreeSelect.vue';
import MultiEditValueRow from './MultiEditValueRow.vue';
import MostaContextMenu from '@/components/common/basic/MostaContextMenu.vue';
import LoadingSkeleton from '@/components/common/aux-selector/refactored/components/LoadingSkeleton.vue';
import warningCircleSvg from '@/assets/warning-circle.svg';

// Цель правки в объединённом окне. Для параметра сам параметр хранится в parameterLink.
export type MultiEditTargetKind = "none" | "parameter" | "details" | "comment";

@Component({
  name: "MultiEditDialog",
  components: {
    RecursiveSelector,
    MultiEditValueRow,
    NativeInput,
    MostaSelect,
    MostaTreeSelect,
    MostaContextMenu,
    LoadingSkeleton,
  },
})
export default class MultiEditDialog extends Vue {

  // model data
  @Prop({ default: [] })
  entities!: number[];
  @Prop({ default: i18n.global.t("edit_entities") })
  headerText!: string;
  // 1 - add tags; 2 - parameters action; 3 - delete tags
  @Prop({ default: 1 })
  modelType!: number;
  @Prop({ default: undefined })
  srcElement!: BaseListItem;
  @Prop({ default: false })
  allChecked!: boolean;
  // 1 - add parameters; 2 - change parameter; 3 - delete parameters
  @Prop({ default: 1 })
  parameterMode!: number;
  // Стартовый режим правки комментария: дописать или заменить
  @Prop({ default: MultiEditStorageDetailsEditType.Append })
  commentActionType!: MultiEditStorageDetailsEditType;
  @Prop({ default: 0 })
  totalElements!: number;
  // Колонка табличного комментария есть в таблице, значит цель "Комментарий" доступна
  @Prop({ default: false })
  hasCommentColumn!: boolean;
  // Права на правку инфо: полный доступ к складу плюс изменение элементов выборки
  @Prop({ default: false })
  detailsEditAllowed!: boolean;
  @Emit("selectorClosed") emitSelectorClosed(arg: any) {
    return arg;
  }
  @Emit("refreshNeeded") emitTableRefresh(arg: any) {
    return arg;
  }

  // store for search functions
  auxstore: any | undefined = undefined;
  storage_pos: any | undefined = undefined;
  storage_move: any | undefined = undefined;
  analytics_comments: any | undefined = undefined;
  filter_string: string = "";
  aux_filter_string: string = "";
  store: any | undefined = undefined;
  check_passed: boolean = false;
  operation_passed: boolean = false;

  // tags
  tags: any[] = [];
  system_tags: CommonEntity[] = [];
  parameterLink: any = { IdChain: [] };
  parameterByLink: any;

  tempTagStatus: any = undefined;

  parameterContainer: ParameterInstanceCreateContainer | undefined = undefined;
  parametersList: ParameterInstanceCreateContainer[] = [];

  msgText: string = "";
  parameterSelectorModel: boolean = false;
  forbidden: boolean = false;

  // parameters
  paramRows: ParamsRow[] = reactive([]);
  vsetType?: V2ParameterValueSetExtensionType = undefined;
  vsetTitle?: string = "";
  vsetOptions: CommonEntity[] = reactive([] as CommonEntity[]);
  vsetLoading = false;

  // comments
  commentChangeType: CommonEntity = {
    Name: i18n.global.t('comment_mode_replace'),
    Id: MultiEditStorageDetailsEditType.Rewrite,
  }; // 0 - изменить; 1 - дописать
  innerNote: string = "";
  lastCommentCaretPosition: number = 0;
  comment_templates = reactive([]);
  private _blockCaretUpdate = false;

  commentTemplateMenuOpen = false;
  commentTemplateMenuPos: { top: string; left: string } = { top: "0px", left: "0px" };

  tids: number[] = [];

  // Запрос действия ещё не ответил, окно открыто
  actionRunning = false;

  // unified window
  editTarget: { kind: MultiEditTargetKind } = { kind: "none" };
  parametersLoading = false;
  // Права на параметры проверяются один раз за окно, дерево грузится один раз
  private parametersChecked = false;
  private parametersLoaded = false;

  // rules
  intRules = [
    (val: any) => !this.isNumeric(val) || i18n.global.t("enter_integer"),
  ];
  floatRules = [
    (val: any) => !this.isNumericFloat(val) || i18n.global.t("input_float"),
  ];

  // analytics lists
  listMenu: any = undefined;
  listModel: SelectionEntityWithRolesFilterSend = {
    Entity: { Name: "", Id: 0 },
    Roles: [],
    Positive: true,
  };

  get isPromtDialog() {
    return (
      (this.modelType === 5 && this.parameterMode === 2) || this.modelType === 4
    );
  }

  get isParameterChangeMode(): boolean {
    return (
      (this.modelType === 15 && this.parameterMode === 2) ||
      (this.isUnifiedEditWindow && this.editTarget.kind === "parameter")
    );
  }

  get isTagsMode(): boolean {
    return this.modelType === 1;
  }

  get primaryActionLabel(): string {
    if (this.isParameterChangeMode || this.isTagsMode || !this.isPromtDialog) {
      return this.$t("save");
    }
    return this.$t("confirm_short");
  }

  warningIcon = warningCircleSvg;
  // Пока идёт проверка прав, в окне подтверждения нечего показать кроме статуса.
  checkingRights = false;

  // Удаление элементов подтверждаем красной кнопкой, остальные действия зелёной.
  get isDangerAction(): boolean {
    return this.modelType === 4;
  }

  // В тексте подтверждения выделяем слово про безвозвратность, чтобы его не пролистали
  // глазами. Текст собирается только из словаря и числа отобранных объектов, но экранируем
  // на всякий случай, раз выводим через v-html.
  get promptTextHtml(): string {
    const escaped = (this.msgText || "").replace(
      /[&<>]/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string)
    );
    const word = String(this.$t("permanently_word"));
    const idx = word ? escaped.indexOf(word) : -1;
    if (idx < 0) return escaped;
    return (
      escaped.slice(0, idx) +
      "<b>" +
      word +
      "</b>" +
      escaped.slice(idx + word.length)
    );
  }

  // Заголовок окна подтверждения задаёт само действие, а не пункт меню.
  get promptTitle(): string {
    if (this.isDangerAction) return this.$t("delete_from_system_question");
    if (this.modelType === 5 && this.parameterMode === 2) {
      return this.$t("delete_from_list_question");
    }
    return this.headerText;
  }

  // Объединённое окно шире остальных режимов: в одной строке цель правки и режим,
  // ниже многострочный ввод.
  get cardSizeStyle(): string {
    const wideWindow =
      this.isUnifiedEditWindow ||
      (this.modelType === 15 && [1, 3].includes(this.parameterMode)) ||
      this.isTagsMode ||
      (this.modelType === 5 && this.parameterMode === 1);
    return wideWindow
      ? "max-width: 420px; min-width: 420px;"
      : "max-width: 250px; min-width: 250px;";
  }

  // Правка инфо, табличного комментария и значения параметра живут в одном окне
  get isUnifiedEditWindow(): boolean {
    return (
      this.modelType === 6 || (this.modelType === 15 && this.parameterMode === 2)
    );
  }

  // Сохранять нечего, пока не выбрана цель правки или сами параметры
  get saveDisabled(): boolean {
    if (this.isUnifiedEditWindow) {
      if (this.editTarget.kind === "none") return true;
      return this.editTarget.kind === "parameter" && !this.parameterContainer;
    }
    if (this.modelType === 15 && [1, 3].includes(this.parameterMode)) {
      return !this.multipleParametersModel?.length;
    }
    return false;
  }

  get isNotesTarget(): boolean {
    return this.editTarget.kind === "details" || this.editTarget.kind === "comment";
  }

  get canTargetDetails(): boolean {
    return [7, 8].includes(this.srcElement?.DestType) && this.detailsEditAllowed;
  }

  get canTargetComment(): boolean {
    return true;
  }

  // Дерево параметров есть только у тех сегментов, которые поддерживает
  // getParametersContainer в сторе массовой правки
  get canTargetParameters(): boolean {
    return [0, 1, 7].includes(this.srcElement?.DestType);
  }

  get isListParameter(): boolean {
    return !!this.parameterContainer?.IsList;
  }

  get targetDisplayText(): string {
    switch (this.editTarget.kind) {
      case "details":
        return this.$t("details_type");
      case "comment":
        return this.$t("notes_type");
      case "parameter":
        return this.parameterModelStr;
      default:
        return "";
    }
  }

  // Заголовок считается по текущей цели, иначе после переключения он врёт
  get windowTitle(): string {
    if (!this.isUnifiedEditWindow) return this.headerText;
    switch (this.editTarget.kind) {
      case "parameter":
        return this.$t("edit_parameter");
      case "details":
        return this.$t("change_details");
      case "comment":
        return this.$t("change_comment");
      default:
        return this.headerText;
    }
  }

  // Шаблоны инфо у позиций и движений разные, у табличного комментария свой набор
  get commentTemplateModelType(): number {
    if (this.editTarget.kind === "comment") return 32;
    return this.srcElement?.DestType === 7 ? 24 : 25;
  }

  get templatesTooltipKey(): string {
    return this.editTarget.kind === "comment"
      ? "comment_templates"
      : "info_templates";
  }

  get startEditTarget(): { kind: MultiEditTargetKind } {
    if (this.modelType === 6) {
      return this.parameterMode === 3 ? { kind: "comment" } : { kind: "details" };
    }
    return { kind: "parameter" };
  }

  pickNotesTarget(kind: MultiEditTargetKind) {
    (this.$refs.target_select as any)?.close?.();
    this.setEditTarget({ kind });
  }

  // Смена цели обнуляет накопленное подтверждение: текст относился к прежнему действию
  async setEditTarget(target: { kind: MultiEditTargetKind }) {
    this.editTarget = target;
    this.msgText = "";
    this.check_passed = false;
    this.forbidden = false;

    if (target.kind === "details" || target.kind === "comment") {
      this.check_passed = true;
      return;
    }

    // Дописать имеет смысл только у текстовых инфо и комментария, у остальных целей
    // селектор режима задизейблен и должен показывать заменить
    this.commentChangeType = {
      Name: this.$t("comment_mode_replace"),
      Id: MultiEditStorageDetailsEditType.Rewrite,
    };

    if (target.kind === "parameter") {
      await this.ensureParametersReady();
    }
  }

  async ensureParametersReady() {
    if (this.parametersChecked) return;

    if (this.srcElement?.DestType !== 7) {
      // Пока идёт проверка прав, дерево параметров ещё не готово: держим скелетон
      this.parametersLoading = !this.parametersLoaded;
      const res = await this.store.preCheckParametersAccess(this.entitiesContainer);
      if (res?.data?.Result === PreCheckResult.ActionForbidden) {
        this.parametersLoading = false;
        this.msgText = this.$t("multiedit_param_edit_forbidden");
        this.forbidden = true;
        return;
      }
    }

    this.parametersChecked = true;
    if (!this.parametersLoaded) {
      await this.loadParametersTemplate();
    } else {
      this.parametersLoading = false;
    }
  }

  get selectionCount(): number {
    if (this.allChecked) {
      return Number(this.totalElements) - Number(this.entities?.length || 0);
    }
    return this.entities?.length || 0;
  }

  get pressSaveSuffix(): string {
    return (
      " " +
      this.$t("press_label") +
      " «" +
      this.$t("save") +
      "»" +
      this.$t("to_perform")
    );
  }

  parameterForbiddenKey(): string {
    if (this.parameterMode === 1) return "multiedit_param_add_forbidden";
    if (this.parameterMode === 3) return "multiedit_param_delete_forbidden";
    return "multiedit_param_edit_forbidden";
  }

  applyParameterPrecheckResult(
    action: "edit" | "add" | "delete",
    res: { data?: { Result?: number; AllowedEntitiesCount?: number; ForbiddenEntitiesCount?: number } } | undefined,
  ) {
    const data = res?.data;
    if (!data) return;

    if (data.Result === PreCheckResult.ActionAllowedCompletely) {
      const count = data.AllowedEntitiesCount ?? this.selectionCount;
      this.msgText =
        i18n.global.t(`multiedit_param_${action}_complete`, { count }) +
        (action === "edit" ? this.pressSaveSuffix : "");
      this.check_passed = true;
      this.forbidden = false;
    } else if (data.Result === PreCheckResult.ActionAllowedPartially) {
      this.msgText =
        i18n.global.t(`multiedit_param_${action}_partial`, {
          allowed: data.AllowedEntitiesCount,
          forbidden: data.ForbiddenEntitiesCount,
        }) + (action === "edit" ? this.pressSaveSuffix : "");
      this.check_passed = true;
      this.forbidden = false;
    } else if (data.Result === PreCheckResult.ActionForbidden) {
      this.msgText = this.$t(`multiedit_param_${action}_forbidden`);
      this.forbidden = true;
      this.check_passed = false;
    }
  }

  get entitiesContainer(): any {
    const request: any = {
      EntityIds: this.entities,
      TakeAnything: this.allChecked,
      Segment: this.srcElement.DestType,
    };
    if (this.allChecked) {
      request.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6, 7].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }
    return request;
  }

  // У параметра-списка всегда должна быть хотя бы одна строка, иначе не за что нажать плюс
  seedListRowIfNeeded() {
    if (this.isListParameter && !this.paramRows.length) {
      this.addParamRow();
    }
  }

  openCommentTemplateMenu(ev: MouseEvent) {
    const rect = (this.$refs.commentTemplateBtn as HTMLElement | undefined)?.getBoundingClientRect();
    if (rect) {
      this.commentTemplateMenuPos = { top: `${rect.top}px`, left: `${rect.left}px` };
    } else {
      this.commentTemplateMenuPos = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
    }
    this.commentTemplateMenuOpen = true;
    // Реальную ширину меню знаем только после рендера, поэтому окончательную
    // позицию считаем в nextTick: до отрисовки браузером, без мигания.
    this.$nextTick(() => this.adjustCommentTemplateMenuPosition());
  }

  closeCommentTemplateMenu() {
    this.commentTemplateMenuOpen = false;
  }

  // Меню уходит влево от кнопки, чтобы не накрывать её собой, и держится в пределах экрана.
  adjustCommentTemplateMenuPosition() {
    const MARGIN = 8;
    const inner = document.querySelector(".med-comment__tmpl-menu");
    const menuEl = inner?.closest(".mosta-context-menu") as HTMLElement | null;
    const rect = (this.$refs.commentTemplateBtn as HTMLElement | undefined)?.getBoundingClientRect();
    if (!menuEl) return;

    const menuW = menuEl.offsetWidth || 200;
    const menuH = menuEl.offsetHeight || 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect ? rect.left - menuW - 4 : parseFloat(this.commentTemplateMenuPos.left) || 0;
    let top = rect ? rect.top : parseFloat(this.commentTemplateMenuPos.top) || 0;

    if (rect && left < MARGIN) {
      left = rect.right + 4;
    }

    left = Math.max(MARGIN, Math.min(left, vw - menuW - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - menuH - MARGIN));

    this.commentTemplateMenuPos = { top: `${top}px`, left: `${left}px` };
  }

  windowKeyDown(e: KeyboardEvent) {
    if (e.key == "ArrowLeft") {
      setTimeout(() => {
        (this.$refs.cancelbtn as HTMLElement | undefined)?.focus();
      }, 100);
    } else if (e.key == "ArrowRight") {
      setTimeout(() => {
        (this.$refs.okbtn as HTMLElement | undefined)?.focus();
      }, 100);
    }
  }

  // Пока поверх окна есть дропдаун, Esc принадлежит ему. Когда слоёв не осталось -
  // то же, что Отмена. NativeInput сам глотает Esc, пока поле в режиме правки.
  onDocumentEscape = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (document.querySelector(".confirm-popup")) return;
    if (
      document.querySelector(
        ".dp__menu, .dp__menu_wrap, .mosta-select__dropdown, .mtree-select__dropdown, .selector-popup, .mosta-context-menu, .q-menu",
      )
    ) {
      return;
    }
    const ae = document.activeElement as HTMLElement | null;
    if (ae?.closest?.(".inner-editable, [contenteditable='true']")) return;

    e.preventDefault();
    e.stopPropagation();
    this.closeItemMenu();
  }

  onCaretPositionChange(val: number) {
    if (val >= 0 && !this._blockCaretUpdate) {
      this.lastCommentCaretPosition = +val;
    }
  }

  // width function
  get changeCommentOptions() {
    return [
      {
        Name: this.$t('comment_mode_replace'),
        Id: MultiEditStorageDetailsEditType.Rewrite,
      },
      {
        Name: this.$t('comment_mode_append'),
        Id: MultiEditStorageDetailsEditType.Append,
      },
    ];
  }

  @Watch("comment_templates")
  comment_template_watcher() {
    if (!this.comment_templates.length) {
      return;
    }

    const templateText = (this.comment_templates[0] as StorageDetailsTemplateDto).Text;
    // У табличного комментария в контракте нет поля под использованные шаблоны, не копим.
    if (this.editTarget.kind === "details") {
      this.tids.push((this.comment_templates[0] as StorageDetailsTemplateDto).Id);
    }

    const original = this.innerNote || "";

    let c_pos = this.lastCommentCaretPosition;
    if (c_pos < 0) {
      c_pos = 0;
    } else if (c_pos > original.length) {
      c_pos = original.length;
    }

    this._blockCaretUpdate = true;
    this.innerNote =
      original.slice(0, c_pos) + templateText + original.slice(c_pos);
    this.lastCommentCaretPosition = c_pos + templateText.length;
    this.comment_templates.splice(0);

    this.closeCommentTemplateMenu();
    this.$nextTick(() => {
      this._blockCaretUpdate = false;
    });
  }

  onCommentBlur() {
    this._blockCaretUpdate = true;
    setTimeout(() => {
      this._blockCaretUpdate = false;
    }, 0);
  }

  onNativeInputClick() {
    setTimeout(() => {
      // @ts-ignore
      const ref = this.$refs.comment_field as any;
      const field = ref?.$refs?.editable_field as HTMLElement | null;
      if (!field) return;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const pre = range.cloneRange();
        pre.selectNodeContents(field);
        pre.setEnd(range.endContainer, range.endOffset);
        this.lastCommentCaretPosition = pre.toString().length;
      }
    }, 0);
  }

  async addCommentTemplate(t_name: string) {
    if (t_name && t_name.length && this.innerNote && this.innerNote.length) {
      if (this.editTarget.kind === "comment") {
        this.analytics_comments.createCommentTemplate({
          Name: t_name,
          Text: this.innerNote,
          Segment: this.srcElement.DestType,
        });
      } else if (this.srcElement.DestType === 7) {
        // positions
        this.storage_pos.createNoteTemplate({
          Name: t_name,
          Text: this.innerNote,
        });
      } else if (this.srcElement.DestType === 8) {
        this.storage_move.createMovementNoteTemplate({
          Name: t_name,
          Text: this.innerNote,
        });
      }
    }
  }

  async deleteCommentTemplate(t_id: number) {}

  // multiple parameters selection
  parametersMultipleValues: any[] = [];

  isNumeric(str: string) {
    if (typeof str != "string") return false; // we only process strings!
    return (
      !isNaN(+str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
      !isNaN(parseInt(str))
    ); // ...and ensure strings of whitespace fail
  }

  isNumericFloat(str: string) {
    if (typeof str != "string") return false; // we only process strings!
    return (
      !isNaN(+str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
      !isNaN(parseFloat(str))
    ); // ...and ensure strings of whitespace fail
  }

  @Watch("filter_string")
  // change focus from search field to list when up and down arrow keys are pressed
  focusPanel() {
    const that = this;
    setTimeout(() => {
      that.$refs.rec_selector &&
        (that.$refs.rec_selector as any).trackArrowDown(1, true);
    }, 100);
  }

  async created() {
    this.store = multiEditStore();
    this.auxstore = auxStore();
    this.storage_pos = storagePositionsStore();
    this.storage_move = storageMovementsStore();
    this.analytics_comments = analyticsCommentsStore();

    this.paramRows = reactive([]);
    this.tids = [];

    if (this.auxstore && this.modelType != 5) {
      this.system_tags = reactive(
        (await this.auxstore.getTagContainer()).data as TagContainer,
      ).AllSystemTags;
    }
  }

  hideListMenu() {
    (this.$refs.list_select as any)?.close?.();
    setTimeout(() => {
      (this.$refs.okbtn as HTMLElement | undefined)?.focus();
    }, 50);
  }

  get listDisplayText(): string {
    return this.listModel?.Entity?.Name || "";
  }

  onListSelectEnter() {
    if (this.aux_filter_string?.trim()) {
      this.onUpdateListModelNew(this.aux_filter_string);
      this.hideListMenu();
      return;
    }
    (this.$refs.aux_selector as any)?.selectCurrent?.();
  }

  onListFreeInput(val?: string) {
    const name = (val || this.aux_filter_string || "").trim();
    if (!name) return;
    this.onUpdateListModelNew(name);
    this.hideListMenu();
  }

  onUpdateListModel(arg: any) {
    arg && arg.length && (this.listModel = arg[0]);

    localStorage.setItem(
      "last_selected_anlist_" + this.srcElement.DestType,
      JSON.stringify(this.listModel.Entity),
    );
  }

  onUpdateListModelNew(arg: string) {
    this.listModel = {
      Entity: { Name: arg, Id: -1 },
      Roles: [],
      Positive: true,
    };

    localStorage.setItem(
      "last_selected_anlist_" + this.srcElement.DestType,
      JSON.stringify(this.listModel.Entity),
    );
  }

  get tagsDisplayText(): string {
    return this.tags.map((el) => this.tagChipLabel(el)).join(", ");
  }

  tagChipLabel(el: any): string {
    return typeof el === "string" ? el : el?.Name || "";
  }

  get tagsSelectorModel(): any[] {
    return this.tags
      .filter((el) => typeof el === "object" && el)
      .map((el) => ({
        Entity: el,
        Roles: [],
        Positive: true,
      }));
  }

  set tagsSelectorModel(val: any[]) {
    this.check_passed = false;
    const strings = this.tags.filter((el) => typeof el === "string");
    const entities = (val || [])
      .map((v) => v?.Entity ?? v)
      .filter((el) => el && typeof el === "object");
    this.tags = [...entities, ...strings];
  }

  removeSelectedTag(index: number) {
    this.check_passed = false;
    this.tags.splice(index, 1);
  }

  closeTagsSelect() {
    (this.$refs.tags_select as any)?.close?.();
  }

  onAddFreeTag(val: string) {
    const name = (val || "").trim();
    if (!name) return;
    this.check_passed = false;
    const found = this.system_tags.find((el) => el.Name.trim() === name);
    if (found) {
      const exists = this.tags.some(
        (el) => typeof el === "object" && el?.Id === found.Id,
      );
      if (!exists) this.tags.push(found);
      return;
    }
    const existsText = this.tags.some(
      (el) => typeof el === "string" && el.trim() === name,
    );
    if (!existsText) this.tags.push(name);
  }

  // tag status flags filter
  tagStatusName(arg: number): string {
    switch (arg) {
      case TagType.Standard:
        return this.$t("normal_label");
      case TagType.Hidden:
        return this.$t("hidden_upcase");
      default:
        return this.$t("status_unknown");
    }
  }

  get tagStatusOptions() {
    return Object.values(TagType)
      .filter((v) => !isNaN(Number(v)) && Number(v) >= 0)
      .map((vm) => {
        return {
          label: this.tagStatusName(Number(vm)),
          value: Number(vm),
        };
      });
  }

  get tagStatusModel() {
    return this.tempTagStatus;
  }

  set tagStatusModel(arg: any) {
    this.check_passed = false;
    this.tempTagStatus = arg;
  }

  // single
  get parametersModel(): any {
    return [this.parameterLink?.IdChain];
  }

  set parametersModel(val: any) {
    this.check_passed = false;
    if (this.parameterLink) {
      this.parameterSelectorModel = false;
      this.parameterLink = { IdChain: val[0] };
    }

    if (this.isUnifiedEditWindow) {
      (this.$refs.target_select as any)?.close?.();
      this.setEditTarget({ kind: "parameter" });
      // parameterContainer резолвится побочно в геттере parameterModelStr, читаем его
      // здесь же: строки значений и скелетон набора нужны до первого клика по полю
      void this.parameterModelStr;
      this.vsetLoading = !!this.parameterContainer?.ValueSet;
      this.seedListRowIfNeeded();
    }
  }

  addParamRow() {
    this.paramRows.push({
      stringValue: "",
      dateValueModel: "",
      checkBoxModel: false,
      // value set models
      vsetValues: [],
      dateValueSetModel: "",
    });
  }

  deleteParamRow(del_index: number) {
    this.paramRows.splice(del_index, 1);
  }

  // multiple
  get multipleParametersModelChains(): any {
    return this.parametersMultipleValues.map((el: any) => el.chain);
  }

  get multipleParametersModel(): any {
    return this.parametersMultipleValues;
  }

  set multipleParametersModel(val: any) {
    this.parametersMultipleValues = reactive(val);
  }

  get multipleParametersDisplayText(): string {
    return this.parametersMultipleValues.map((el: any) => el.Name).join(", ");
  }

  removeSelectedParameter(index: number) {
    this.check_passed = false;
    this.parametersMultipleValues.splice(index, 1);
  }

  set multipleParametersModelChains(val: any) {
    if (!val) {
      return;
    }
    this.check_passed = false;

    this.parametersMultipleValues = val.map((el: any) => {
      const found_element = findParametersByChain(
        el,
        this.parametersList,
        "RecordFields",
        "Template",
      );
      return {
        Id: found_element
          ? found_element.map((el: any) => el.Template.Id).join("_")
          : 0,
        Name: found_element
          ? found_element.map((el: any) => el.Template.Name).join(".")
          : "",
        chain: el,
      };
    });
  }

  @Watch("paramRows", { deep: true })
  params_watcher(arg: string) {
    this.check_passed = false;
  }

  @Watch("parameterContainer", { deep: true })
  async getVSetOptions() {
    this.vsetOptions = reactive([]);
    this.vsetType = undefined;
    this.vsetTitle = "";

    if (
      !this.store ||
      !this.parameterContainer ||
      !this.parameterContainer.ValueSet
    ) {
      this.vsetLoading = false;
      return;
    }

    this.vsetLoading = true;
    const res = await this.store.getValueSetHints(
      this.parameterContainer.ValueSet.Id,
    );
    this.vsetLoading = false;

    res &&
      (this.vsetOptions = reactive(
        (res.data as ParameterValueSetDefaultHintContainer).DefaultHints.sort(
          (el1, el2) => el1.UsageCount - el2.UsageCount,
        ).map((el) => {
          // Имя отдаём целиком: в списке оно режется по ширине дропдауна, полный текст
          // показывает подсказка
          return {
            Id: el.Id,
            Name: el.Name,
          };
        }),
      ));
    this.vsetType = (
      res.data as ParameterValueSetDefaultHintContainer
    ).HintedSetType;
    this.vsetTitle = (
      res.data as ParameterValueSetDefaultHintContainer
    ).HintedSetName;
  }

  // precheck and perform
  // Кнопка действия ждёт ответ: до его прихода окно ещё открыто, а повторный клик
  // отправил бы то же самое второй раз
  async performAction() {
    if (this.operation_passed || this.actionRunning) {
      return;
    }

    this.actionRunning = true;
    try {
      await this.dispatchAction();
    } finally {
      this.actionRunning = false;
    }
  }

  async dispatchAction() {
    if (this.isUnifiedEditWindow) {
      await this.performUnifiedAction();
      return;
    }

    switch (this.modelType) {
      case 1:
        if (this.parameterMode === 1) {
          if (this.check_passed) {
            await this.addTagsToEntities();
          } else {
            await this.checkAddTagsToEntities();
          }
        } else if (this.parameterMode === 2) {
          if (this.check_passed) {
            await this.deleteTagsFromEntities();
          } else {
            await this.checkDeleteTagsFromEntities();
          }
        } else if (this.parameterMode === 3) {
          if (this.check_passed) {
            await this.changeEntityTagsStatus();
          } else {
            await this.checkEntityTagsStatus();
          }
        }
        break;
      case 5:
        if (this.parameterMode === 1) {
          await this.addEntitiesToList();
        } else if (this.parameterMode === 2) {
          if (this.check_passed) {
            await this.removeEntitiesFromList();
          } else {
            await this.checkRemoveEntitiesFromList();
          }
        }
        break;
      case 4:
        if (this.check_passed) {
          await this.removeEntities();
        } else {
          await this.checkRemoveEntities();
        }
        break;
      case 15:
        if (this.parameterMode === 2) {
          if (this.check_passed || this.srcElement.DestType === 7) {
            await this.changeEntitiesParameter();
          } else {
            await this.checkChangeParameter();
          }
        } else if (this.parameterMode === 1) {
          if (this.check_passed) {
            await this.addEntitiesParameters();
          } else {
            await this.checkAddParameters();
          }
        } else if (this.parameterMode === 3) {
          if (this.check_passed) {
            await this.deleteEntitiesParameters();
          } else {
            await this.checkDeleteParameters();
          }
        }
        break;
      default:
        break;
    }
  }

  // Объединённое окно маршрутизирует сохранение по выбранной цели
  async performUnifiedAction() {
    switch (this.editTarget.kind) {
      case "details":
        // Складское инфо есть только у позиций и движений
        if (this.check_passed && [7, 8].includes(this.srcElement.DestType)) {
          await this.changeComment();
        }
        break;
      case "comment":
        if (this.check_passed) {
          await this.changeTableComments();
        }
        break;
      case "parameter":
        if (this.check_passed || this.srcElement.DestType === 7) {
          await this.changeEntitiesParameter();
        } else {
          await this.checkChangeParameter();
        }
        break;
      default:
        break;
    }
  }

  // tags aactions
  checkAddTagsToEntities() {
    if (!this.tags || !this.tags.length) {
      return;
    }

    this.msgText =
      i18n.global.t("multiedit_tags_add_complete", {
        count: this.selectionCount,
      }) + this.pressSaveSuffix;

    this.check_passed = true;
  }

  async addTagsToEntities() {
    if (!this.tags || !this.tags.length) {
      return;
    }

    const text_array = this.tags.filter((el: any) => {
      return typeof el === "string";
    });
    const entity_array = this.tags.filter((el: any) => {
      return typeof el === "object";
    });

    let request: any = {
      NewTags: text_array,
      TagIds: entity_array.map((el) => el.Id),
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request);
    await this.store.addTags(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    this.emitTableRefresh(true);
  }

  async changeComment() {
    if (this.editTarget.kind === "comment") {
      await this.changeTableComments();
      return;
    }

    let request: any = {
      ActionType: this.commentChangeType.Id,
      Details: this.innerNote,
      UsedDetailsTemplates: this.tids,
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    if (this.srcElement.DestType === 7) {
      await this.store.changeStorageItemsComment(request);
    } else if (this.srcElement.DestType === 8) {
      await this.store.changeItemMovementsComment(request);
    }

    this.tids.splice(0);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    // Цель окна переключается на ходу, поэтому режим обновления таблицы едет за целью,
    // а не за пунктом меню: инфо движений требует пересчёта, инфо позиций нет
    this.emitTableRefresh({ recalc: this.srcElement?.DestType === 8 });
  }

  async changeTableComments() {
    const request: MultiEditCommentsRequest = {
      ActionType: this.commentChangeType.Id,
      CommentValue: this.innerNote,
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    const isChart = [BaseItemTypes.GRAPH, BaseItemTypes.PIE_CHART, BaseItemTypes.BAR_CHART]
      .includes(this.srcElement.elType);

    if (isChart) {
      request.ChartIdentity = {
        ChartId: this.srcElement.Id,
        ChartType: this.srcElement.elType === 2 ? AnalyticsV2ChartType.Linear
          : this.srcElement.elType === 3 ? AnalyticsV2ChartType.Pie
          : AnalyticsV2ChartType.Bar,
        Destination: this.srcElement.DestType,
      } as AnalyticsChartIdentity;
    } else {
      const identity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;

      request.ElementIdentity = identity;
      request.Container.ElementIdentity = identity;
    }

    // У нагрузки строки таблицы вычисляются из сектора или точки кривой, поэтому
    // отдаём те же параметры раскрытия, с которыми таблица была построена.
    if (this.srcElement.DestType === AnalyticsV2ElementType.Workload) {
      request.WorkloadChartTableRequest = this.auxstore.last_workload_asker;
      request.ChartType = this.auxstore.last_workload_chart_type;
    }

    await this.store.changeTableComments(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    // Табличный комментарий на состав строк не влияет, пересчёт не нужен
    this.emitTableRefresh({ recalc: false });
  }

  checkAddEntitiesToList() {
    if (!this.totalElements) {
      return;
    }

    if (this.listModel && this.listModel.Entity) {
      if (this.listModel.Entity.Id <= 0 && !this.listModel.Entity.Name.length) {
        this.msgText = this.$t("please_select_list");
        return;
      }
    } else {
      this.msgText = this.$t("please_select_list");
      return;
    }

    this.msgText =
      (this.allChecked
        ? this.$t("all_entities") +
          (this.entities.length
            ? this.$t("except_label") + " " + this.entities.length + ","
            : "") +
          " " +
          this.$t("will_be_added_to_list")
        : this.$t("following_entities_count") +
          " (" +
          this.entities.length +
          this.$t("pcs") +
          ") " +
          this.$t("will_be_added_to_list")) +
      ".";

    this.check_passed = true;
  }

  async addEntitiesToList() {
    if (this.listModel && this.listModel.Entity) {
      if (this.listModel.Entity.Id <= 0 && !this.listModel.Entity.Name.length) {
        this.msgText = this.$t("please_select_list");
        return;
      }
    } else {
      this.msgText = this.$t("please_select_list");
      return;
    }

    if (!this.listModel || !this.listModel.Entity || !this.totalElements) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    if (this.listModel.Entity.Id > 0) {
      request.ListId = this.listModel.Entity.Id;
    } else if (this.listModel.Entity.Name.length) {
      request.NewListName = this.listModel.Entity.Name;
    }

    request.Container.ElementIdentity = {
      ElementId: this.srcElement.Id,
      Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
        ? this.srcElement.SrcType
        : null,
      Destination: this.srcElement.DestType,
      Kind: getIdentityKind(this.srcElement),
    } as AnalyticsElementIdentity;

    await this.store.addEntitiesToList(request);

    /* this.msgText = this.$t('action_done');
      this.check_passed = false;
      this.operation_passed = true; */
    this.closeItemMenu();
  }

  checkRemoveEntitiesFromList() {
    if (!this.totalElements) {
      return;
    }

    this.msgText =
      (this.allChecked
        ? this.$t("all_entities") +
          (this.entities.length
            ? this.$t("except_label") + " " + this.entities.length + ","
            : "") +
          " " +
          this.$t("will_be_removed_from_list")
        : this.$t("following_entities_count") +
          " (" +
          this.entities.length +
          this.$t("pcs") +
          ") " +
          this.$t("will_be_removed_from_list")) +
      ".";

    this.check_passed = true;
  }

  async removeEntitiesFromList() {
    if (!this.totalElements) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    request.Container.ElementIdentity = {
      ElementId: this.srcElement.Id,
      Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
        ? this.srcElement.SrcType
        : null,
      Destination: this.srcElement.DestType,
      Kind: getIdentityKind(this.srcElement),
    } as AnalyticsElementIdentity;

    await this.store.deleteEntitiesFromList(request);

    /* this.msgText = this.$t('action_done');
      this.check_passed = false;
      this.operation_passed = true;
      */
    this.emitTableRefresh({
      recalc: true,
      clearSelection: true,
    });
    this.closeItemMenu();
  }

  checkRemoveEntities() {
    if (!this.allChecked && (!this.entities || !this.entities.length)) {
      return;
    }

    // this.msgText = this.$t('will_be_removed')+
    // (this.allChecked ? ' '+this.$t('all_entities') + (this.entities.length ? ', '+this.$t('except_label')+' '+this.entities.length : '') :
    // ' '+this.$t('following_entities_count')+' '+this.entities.length+' '+this.$t('pcs'))+
    // '. '+this.$t('press_label')+' "'+this.$t('continue')+'"'+this.$t('to_perform');

    this.msgText =
      (this.allChecked
        ? this.$t("all_entities") +
          (this.entities.length
            ? this.$t("except_label") + " " + this.entities.length + ","
            : "") +
          " " +
          this.$t("will_be_removed")
        : this.$t("following_entities_count") +
          " (" +
          this.entities.length +
          this.$t("pcs") +
          ") " +
          this.$t("will_be_removed")) +
      " " +
      this.$t("not_only_selection") +
      ".";

    this.check_passed = true;
  }

  async removeEntities() {
    if (!this.allChecked && (!this.entities || !this.entities.length)) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request.Container);
    await this.store.removeEntities(request.Container);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    this.emitTableRefresh({
      recalc: true,
      clearSelection: true,
    });
  }
  checkEntityTagsStatus() {
    if (!this.allChecked && (!this.entities || !this.entities.length)) {
      return;
    }

    if (!this.tempTagStatus) {
      this.msgText = this.$t("status_needed");
      this.check_passed = true;
      return;
    }
    this.msgText =
      i18n.global.t("multiedit_tags_status_complete", {
        count: this.selectionCount,
      }) + this.pressSaveSuffix;
    this.check_passed = true;
  }

  async changeEntityTagsStatus() {
    if (!this.allChecked && (!this.entities || !this.entities.length)) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      NewStatus: this.tempTagStatus.value,
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request);
    await this.store.changeTagsStatus(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    this.emitTableRefresh(true);
  }

  checkDeleteTagsFromEntities() {
    if (!this.tags || !this.tags.length) {
      return;
    }

    this.msgText =
      i18n.global.t("multiedit_tags_delete_complete", {
        count: this.selectionCount,
      }) + this.pressSaveSuffix;

    this.check_passed = true;
  }

  async deleteTagsFromEntities() {
    if (!this.tags || !this.tags.length) {
      return;
    }

    const text_array = this.tags.filter((el: any) => {
      return typeof el === "string";
    });
    const entity_array = this.tags.filter((el: any) => {
      return typeof el === "object";
    });

    let request: any = {
      NewTags: text_array,
      TagIds: entity_array.map((el) => el.Id),
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request);
    await this.store.deleteTags(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    this.emitTableRefresh(true);
  }

  // parameters actions
  async checkChangeParameter() {
    if (
      !this.parameterContainer ||
      !this.parameterLink ||
      !this.parameterLink.IdChain ||
      !this.parameterLink.IdChain.length
    ) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      ParameterLink: this.parameterLink,
    };

    request.InstanceValues = this.paramRows
      .filter(
        (el) =>
          this.parameterContainer?.ValueType !==
            V2ParameterValueType.ValueSet ||
          !el.vsetValues ||
          !el.vsetValues.length,
      )
      .map((row: ParamsRow) => {
        switch (this.parameterContainer?.ValueType) {
          case V2ParameterValueType.String:
            return {
              StringValue: row.stringValue || null,
            };
          case V2ParameterValueType.Int:
            return {
              NumberValue: this.isNumeric(row.stringValue)
                ? +row.stringValue
                : null,
            };
          case V2ParameterValueType.Double:
            return {
              NumberValue: this.isNumericFloat(row.stringValue)
                ? +row.stringValue
                : null,
            };
          case V2ParameterValueType.Bool:
            return {
              BoolValue: row.checkBoxModel,
            };
          case V2ParameterValueType.Date:
            return {
              DateValue: row.dateValueModel || null,
            };
          case V2ParameterValueType.ValueSet:
            if (!row.vsetValues.new) {
              return {
                SelectedValueId: row.vsetValues.Id || null,
              };
            } else {
              switch (this.parameterContainer.ValueSet.ValueType) {
                case V2ParameterValueSet_ValueType.String:
                  return {
                    NewCreatedValue: {
                      NameString: row.vsetValues.NameString,
                    },
                  };
                // falls through
                case V2ParameterValueSet_ValueType.Int:
                case V2ParameterValueSet_ValueType.Decimal:
                  return {
                    NewCreatedValue: {
                      NameNumber: row.vsetValues.NameNumber,
                    },
                  };
                case V2ParameterValueSet_ValueType.Date:
                  return {
                    NewCreatedValue: {
                      NameDate: row.vsetValues.NameDate,
                    },
                  };
                default:
                  return null;
              }
            }
          default:
            return null;
        }
      })
      .filter((el) => el !== null);

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request);

    const res = await this.store.preCheckParametersAccess(request.Container);
    this.applyParameterPrecheckResult("edit", res);
  }

  async changeEntitiesParameter() {
    if (
      !this.parameterContainer ||
      !this.parameterLink ||
      !this.parameterLink.IdChain ||
      !this.parameterLink.IdChain.length
    ) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      ParameterLink: this.parameterLink,
    };

    request.InstanceValues = this.paramRows
      .filter(
        (el) =>
          this.parameterContainer?.ValueType !==
            V2ParameterValueType.ValueSet ||
          !el.vsetValues ||
          !el.vsetValues.length,
      )
      .map((row: ParamsRow) => {
        switch (this.parameterContainer?.ValueType) {
          case V2ParameterValueType.String:
            return {
              StringValue: row.stringValue || null,
            };
          case V2ParameterValueType.Int:
            return {
              NumberValue: this.isNumeric(row.stringValue)
                ? +row.stringValue
                : null,
            };
          case V2ParameterValueType.Double:
            return {
              NumberValue: this.isNumericFloat(row.stringValue)
                ? +row.stringValue
                : null,
            };
          case V2ParameterValueType.Bool:
            return {
              BoolValue: row.checkBoxModel,
            };
          case V2ParameterValueType.Date:
            return {
              DateValue: row.dateValueModel || null,
            };
          case V2ParameterValueType.ValueSet:
            if (!row.vsetValues.new) {
              return {
                SelectedValueId: row.vsetValues.Id || null,
              };
            } else {
              switch (this.parameterContainer.ValueSet.ValueType) {
                case V2ParameterValueSet_ValueType.String:
                  return {
                    NewCreatedValue: {
                      NameString: row.vsetValues.NameString,
                    },
                  };
                // falls through
                case V2ParameterValueSet_ValueType.Int:
                case V2ParameterValueSet_ValueType.Decimal:
                  return {
                    NewCreatedValue: {
                      NameNumber: row.vsetValues.NameNumber,
                    },
                  };
                case V2ParameterValueSet_ValueType.Date:
                  return {
                    NewCreatedValue: {
                      NameDate: row.vsetValues.NameDate,
                    },
                  };
                default:
                  return null;
              }
            }
          default:
            return null;
        }
      })
      .filter((el) => el !== null);

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6, 7].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    await this.store.editParameters(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    // Значение параметра может входить в условия выборки, поэтому с пересчётом
    this.emitTableRefresh({ recalc: true });
  }

  async addEntitiesParameters() {
    if (!this.multipleParametersModel || !this.multipleParametersModel.length) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      ParameterTemplateIds: this.multipleParametersModel.map((el: any) => {
        return el.Id;
      }),
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    await this.store.addParameters(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    this.emitTableRefresh(true);
  }

  async checkAddParameters() {
    if (!this.multipleParametersModel || !this.multipleParametersModel.length) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      ParameterTemplateIds: this.multipleParametersModel.map((el: any) => {
        return el.Id;
      }),
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request);

    const res = await this.store.preCheckParametersAccess(request.Container);
    this.applyParameterPrecheckResult("add", res);
  }

  async deleteEntitiesParameters() {
    if (!this.multipleParametersModel || !this.multipleParametersModel.length) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      ParameterTemplateIds: this.multipleParametersModel.map((el: any) => {
        return el.Id;
      }),
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    await this.store.deleteParameters(request);

    this.msgText = this.$t("action_done");
    this.check_passed = false;
    this.operation_passed = true;
    this.emitTableRefresh(true);
  }

  async checkDeleteParameters() {
    if (!this.multipleParametersModel || !this.multipleParametersModel.length) {
      return;
    }

    let request: any = {
      Container: {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      },
      ParameterTemplateIds: this.multipleParametersModel.map((el: any) => {
        return el.Id;
      }),
    };

    if (this.allChecked) {
      request.Container.ElementIdentity = {
        ElementId: this.srcElement.Id,
        Source: [0, 1, 2, 3, 4, 5, 6].includes(this.srcElement.SrcType)
          ? this.srcElement.SrcType
          : null,
        Destination: this.srcElement.DestType,
        Kind: getIdentityKind(this.srcElement),
      } as AnalyticsElementIdentity;
    }

    console.log(request);

    const res = await this.store.preCheckParametersAccess(request.Container);
    this.applyParameterPrecheckResult("delete", res);
  }

  // Имя выбранного параметра. Пустая строка означает, что селектор покажет плейсхолдер.
  get parameterModelStr(): string {
    if (
      !this.parameterLink ||
      !this.parameterLink.IdChain ||
      !this.parameterLink.IdChain.length
    ) {
      return "";
    }

    let fullName = "";
    const param = findRecursive(
      function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined;

        if (
          arrayEquals(
            [...el_to_find.chain, element.Template.Id],
            el_to_find.el_to_find,
          )
        ) {
          fullName = el_to_find.compound_name
            ? el_to_find.compound_name + "." + element.Template.Name
            : element.Template.Name;
          return element;
        }
        if (element.RecordFields) {
          return findRecursive(
            a,
            {
              chain: [...el_to_find.chain, element.Template.Id],
              compound_name: el_to_find.compound_name
                ? el_to_find.compound_name + "." + element.Template.Name
                : element.Template.Name,
              el_to_find: el_to_find.el_to_find,
            },
            element.RecordFields,
          );
        }
        return undefined;
      },
      { chain: [], compound_name: "", el_to_find: this.parameterLink.IdChain },
      this.parametersList,
    );
    this.parameterContainer = param;

    // add just one line if not list
    if (param && !param.IsList) {
      this.paramRows = reactive([
        {
          stringValue: "",
          dateValueModel: "",
          checkBoxModel: false,
          // value set models
          vsetValues: [],
          dateValueSetModel: "",
        },
      ]);
    }

    return param ? fullName : "";
  }

  async mounted() {
    document.addEventListener("keydown", this.onDocumentEscape, true);

    // Кнопка подтверждения обычная, а не q-btn: фокус на открытии ставим руками,
    // чтобы Enter и стрелки работали как раньше.
    this.$nextTick(() => {
      (this.$refs.okbtn as HTMLElement | undefined)?.focus();
    });

    if (this.isUnifiedEditWindow) {
      await this.initUnifiedWindow();
      return;
    }

    if (this.modelType === 5) {
      if (this.parameterMode === 2) {
        this.checkRemoveEntitiesFromList();
      } else if (this.parameterMode === 1) {
        this.check_passed = true;

        // auto recall last remembered element if there is any
        const lastItemStr = localStorage.getItem(
          "last_selected_anlist_" + this.srcElement.DestType,
        );
        let lastItem = lastItemStr
          ? (JSON.parse(lastItemStr) as CommonEntity)
          : { Id: -1, Name: "" };
        if (lastItem && lastItem.Name) {
          // if any
          // find it
          const lists = (
            await this.auxstore.getFilterLists(
              lastItem.Name,
              10,
              0,
              this.srcElement.elType == BaseItemTypes.LIST ? this.srcElement.Id : 0,
              this.srcElement.DestType,
            )
          ).data.Lists;

          const foundEl = lists.find((el: any) => el.Name == lastItem.Name);
          if (foundEl) {
            this.listModel = {
              Entity: foundEl,
              Roles: [],
              Positive: true,
            };
            console.log(foundEl);
          }
          // in case there was a newly created item with Name and no Id
          localStorage.setItem(
            "last_selected_anlist_" + this.srcElement.DestType,
            JSON.stringify(this.listModel.Entity),
          );
        } else {
          const lists = (
            await this.auxstore.getFilterLists(
              "",
              1,
              0,
              this.srcElement.elType == BaseItemTypes.LIST ? this.srcElement.Id : 0,
              this.srcElement.DestType,
            )
          ).data.Lists;
          if (lists.length) {
            this.listModel = {
              Entity: lists[0],
              Roles: [],
              Positive: true,
            };
          }
          console.log(lists[0]);
          // in case there was a newly created item with Name and no Id
          localStorage.setItem(
            "last_selected_anlist_" + this.srcElement.DestType,
            JSON.stringify(this.listModel.Entity),
          );
        }
      }
    } else {
      let request: any = {
        EntityIds: this.entities,
        TakeAnything: this.allChecked,
        Segment: this.srcElement.DestType,
      };
      if (this.allChecked) {
        request.ElementIdentity = {
          ElementId: this.srcElement.Id,
          Source: [0, 1, 2, 3, 4, 5, 6, 7].includes(this.srcElement.SrcType)
            ? this.srcElement.SrcType
            : null,
          Destination: this.srcElement.DestType,
          Kind: getIdentityKind(this.srcElement),
        } as AnalyticsElementIdentity;
      }

      if (this.modelType === 15 && this.srcElement.DestType != 7) {
        const res = await this.store.preCheckParametersAccess(request);
        if (res?.data?.Result === PreCheckResult.ActionForbidden) {
          this.msgText = this.$t(this.parameterForbiddenKey());
          this.forbidden = true;
        } else {
          this.loadParametersTemplate();
        }
      } else if (this.modelType === 15 && this.srcElement.DestType === 7) {
        this.loadParametersTemplate();
      } else if (this.modelType === 4) {
        this.checkingRights = true;
        let res: any;
        try {
          res = await this.store.preCheckRemoveAccess(request);
        } finally {
          this.checkingRights = false;
        }
        if (res?.data?.Result === PreCheckResult.ActionForbidden) {
          this.msgText = this.$t("multiedit_delete_forbidden");
          this.forbidden = true;
        } else {
          this.checkRemoveEntities();
        }
      }
    }
  }

  async initUnifiedWindow() {
    // Режим заменить или дописать задаёт пункт меню, которым открыли окно
    this.commentChangeType =
      this.commentActionType === MultiEditStorageDetailsEditType.Rewrite
        ? {
            Name: this.$t('comment_mode_replace'),
            Id: MultiEditStorageDetailsEditType.Rewrite,
          }
        : {
            Name: this.$t('comment_mode_append'),
            Id: MultiEditStorageDetailsEditType.Append,
          };

    const start = this.startEditTarget;
    await this.setEditTarget(start);

    // Стартовая цель "параметр" уже проверила права и подтянула дерево. В остальных
    // случаях дерево нужно заранее: селектор цели показывает его сразу при открытии.
    if (start.kind !== "parameter" && this.canTargetParameters) {
      await this.loadParametersTemplate();
    }
  }

  // Обязательные параметры нельзя добавлять и удалять, а значение менять можно
  get excludeMandatoryParameters(): boolean {
    return this.modelType === 15 && [1, 3].includes(this.parameterMode);
  }

  async loadParametersTemplate() {
    if (!this.store) {
      return;
    }

    this.parametersLoading = true;
    const response = await this.store.getParametersContainer(this.srcElement?.DestType);
    const templates = (response?.data || []) as ParameterTemplateForMultiEdit[];
    this.parametersList = reactive(
      templates
        .filter((el: ParameterTemplateForMultiEdit) => !this.excludeMandatoryParameters || !el.IsMandatory)
        .map((el: ParameterTemplateForMultiEdit) => el.TemplateContainer),
    );
    this.parametersLoaded = true;
    this.parametersLoading = false;
  }

  closeItemMenu() {
    this.emitSelectorClosed(0);
  }

  beforeUnmount() {
    document.removeEventListener("keydown", this.onDocumentEscape, true);
  }
}
</script>

<style lang="scss">
.q-card.med-dialog-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #F2F2F2;
  background: #FFF;
}

.minw-selector {
  font-size: small !important;
  min-width: fit-content;
}

.q-card__section.med-header {
  padding: 12px;
}

.q-card__section.med-header--prompt {
  padding: 12px 12px 0;
  justify-content: center;
}

.med-header__icon {
  width: 15px;
  height: 15px;
  margin-right: 4px;
  align-self: center;
}

.med-header__title {
  color: #4D4D4D;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
}

.q-card__section.med-prompt-text {
  padding: 10px 12px 0;
  color: #4D4D4D;
  text-align: center;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  word-wrap: break-word;
}

.q-separator.med-separator {
  margin: 0 4px;
  background: #D2D2D2;
}

.q-card__section.med-body {
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.med-body .field-group {
  padding: 0;
  gap: 16px;
}

.q-card__section.med-body .mtree-select-wrapper,
.q-card__section.med-body .mosta-select-wrapper,
.q-card__section.med-body .mosta-select-wrapper.med-unified__mode,
.q-card__section.med-body .mev__bool {
  margin: 0;
}

.med-msg {
  margin-top: 4px;
  color: #3F884F;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  word-wrap: break-word;
}

.q-card__actions.med-actions {
  padding: 12px;
  justify-content: flex-end;
  gap: 8px;
}

.med-btn {
  display: flex;
  height: 32px;
  padding: 0 16px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  user-select: none;
}

.med-btn--cancel {
  background: var(--prompt-secondary-bg);
  color: var(--Text-color-text-secondary);
}

.med-btn--cancel:hover {
  background: var(--prompt-secondary-hover-bg);
}

.med-btn--cancel:active {
  background: var(--prompt-secondary-active-bg);
}

.med-btn--confirm {
  background: var(--prompt-primary-bg);
  box-shadow: 0 -2px 0 0 var(--prompt-primary-inset) inset;
  color: var(--Text-color-text-btn);
}

.med-btn--confirm:hover {
  background: var(--prompt-primary-hover-bg);
}

.med-btn--confirm:active {
  background: var(--prompt-primary-active-bg);
}

.med-btn--confirm:disabled {
  background: var(--prompt-primary-disabled-bg);
  box-shadow: 0 -2px 0 0 var(--prompt-primary-disabled-inset) inset;
  cursor: default;
}

.med-btn--danger {
  background: #F33;
  box-shadow: 0 -2px 0 0 #E61E1E inset;
  color: var(--Text-color-text-btn);
}

.med-btn--danger:hover {
  background: #E61E1E;
  box-shadow: 0 -2px 0 0 #E61E1E inset;
}

.med-btn--danger:active {
  background: #E61E1E;
  box-shadow: 0 -2px 0 0 #E61E1E inset;
}

.med-btn--danger:disabled {
  background: #FF8585;
  box-shadow: 0 -2px 0 0 #F07878 inset;
  cursor: default;
}

.mosta-select-wrapper.med-comment__mode {
  margin: 0 0 8px 0;
}

.med-comment__row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
}

.med-comment .med-comment__input {
  padding: 8px 12px;
  min-height: 32px;
  border-radius: 8px;
  border: 1px solid #D2D2D2;
  background: #FFF;
  color: #4D4D4D;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
}

.med-comment .med-comment__input .native-input__clear-icon {
  font-size: 16px !important;
}

/* Бокс иконки очистки складывается в ноль, сама иконка рисуется поверх содержимого:
   её появление при постановке каретки не должно менять высоту поля */
.med-comment .med-comment__input .native-input__clear-wrap {
  height: 0;
  line-height: 0;
}

.med-comment__tmpl-btn {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0 0 0 2px;
  line-height: 0;
  cursor: pointer;
  color: #979797;
}

.med-comment__tmpl-btn:hover {
  color: #3F884F;
}

.med-comment__tmpl-btn--active {
  color: #3F884F;
}

.med-comment__tmpl-btn:disabled {
  color: #D2D2D2;
  cursor: default;
}

.med-comment__tmpl-btn:disabled:hover {
  color: #D2D2D2;
}

.med-comment__tmpl-ico {
  width: 16px;
  height: 16px;
  display: block;
  background-color: currentColor;
  mask: url("@/assets/icon-comment.svg") no-repeat center / contain;
  -webkit-mask: url("@/assets/icon-comment.svg") no-repeat center / contain;
}

.med-comment__tmpl-menu {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.med-unified__row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 8px;
}

.med-unified__target {
  flex: 1;
  min-width: 0;
}

.mosta-select-wrapper.med-unified__mode {
  margin: 4px 0;
  width: 130px;
  min-width: 130px;
}

.med-unified__pinned {
  display: flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 12px 4px 8px;
  border-radius: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #4D4D4D;
  cursor: pointer;
}

.med-unified__pinned:hover {
  background: #F2F2F2;
}

.med-unified__pinned--active {
  color: #3F884F;
}

.med-unified__sep {
  height: 1px;
  margin: 4px 0;
  background: #F2F2F2;
}

.med-unified__row-btn {
  background: transparent;
  border: none;
  padding: 0;
  margin: 8px 0 0 4px;
  line-height: 0;
  cursor: pointer;
  color: #979797;
}

.med-unified__row-btn:hover {
  color: #3F884F;
}

.med-unified__row-btn--del:hover {
  color: #979797;
}

/* В svg цвет обводки зашит, поэтому подключаем файл маской и красим фоном */
.med-unified__del-ico {
  display: block;
  width: 16px;
  height: 16px;
  background-color: #979797;
  mask: url("@/assets/icon-delete.svg") no-repeat center / contain;
  -webkit-mask: url("@/assets/icon-delete.svg") no-repeat center / contain;
}

.med-unified__row-btn--del:hover .med-unified__del-ico {
  background-color: #D05A4F;
}

.med-multi__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.med-multi__chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px 2px 8px;
  border-radius: 4px;
  background: #F2F2F2;
  color: #4D4D4D;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 18px;
}

.med-multi__chip-btn {
  background: transparent;
  border: none;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  color: #979797;
}

.med-multi__chip-btn:hover {
  color: #3F884F;
}

.q-tooltip.med-tooltip {
  z-index: 10010;
  white-space: nowrap;
  max-width: none;
}
</style>
